import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { MAP_SETTINGS } from '@/constants/localStorage';
import useWebStorage from '@/hooks/useWebStorage';
import { useAuth } from '@/providers/auth-provider';
import { unimplementedFunction } from '@/utils/unimplementedFunction';
import {
  getJsonFromLocalStorage,
  saveJsonInLocalStorage,
} from '@/utils/webStorage';

export enum ParcelOrFsa {
  FSA = 'fsa',
  PARCELS = 'parcels',
}

type ParcelFsaOption = {
  label: string;
  value: ParcelOrFsa;
};

type LocalStorageMapSettings = {
  feature: ParcelOrFsa;
};

export const parcelFsaOptions: Record<ParcelOrFsa, ParcelFsaOption> = {
  fsa: {
    label: 'FSA',
    value: ParcelOrFsa.FSA,
  },
  parcels: {
    label: 'Parcel',
    value: ParcelOrFsa.PARCELS,
  },
};

export type ParcelFsaBaseLayersState = {
  parcelOrFsaToRender: ParcelOrFsa;
  setParcelOrFsaToRender: (value: ParcelOrFsa) => void;
  setShouldHideParcelOwnerNames: (value: boolean) => void;
  setShouldHideFsaLayer: (value: boolean) => void;
  setShouldHideParcelLayer: (value: boolean) => void;
  shouldRenderFsaLayer: boolean;
  shouldRenderParcelLayer: boolean;
  shouldRenderParcelOwnerNames: boolean;
  shouldHideParcelOwnerNames: boolean;
  shouldHideParcelLayer: boolean;
  shouldHideFsaLayer: boolean;
};

export const emptyParcelFsaBaseLayersState: ParcelFsaBaseLayersState = {
  parcelOrFsaToRender: ParcelOrFsa.PARCELS,
  setParcelOrFsaToRender: unimplementedFunction,
  setShouldHideFsaLayer: unimplementedFunction,
  setShouldHideParcelLayer: unimplementedFunction,
  setShouldHideParcelOwnerNames: unimplementedFunction,
  shouldHideFsaLayer: false,
  shouldHideParcelLayer: false,
  shouldHideParcelOwnerNames: false,
  shouldRenderFsaLayer: false,
  shouldRenderParcelLayer: false,
  shouldRenderParcelOwnerNames: false,
};

export const useParcelFsaBaseLayers = (): ParcelFsaBaseLayersState => {
  const { isAnonymous, authStateLoading, userCanAccessOwnerNames } = useAuth();

  const {
    savedValue: localStorageMapSettings,
    updateValue: saveSettingsInLocalStorage,
    isInitialized: areMapSettingsReady,
  } = useWebStorage<LocalStorageMapSettings>({ key: MAP_SETTINGS });

  const [shouldHideParcelOwnerNames, setShouldHideParcelOwnerNames] =
    useState(false);

  const [shouldHideParcelLayer, setShouldHideParcelLayer] = useState(false);
  const [shouldHideFsaLayer, setShouldHideFsaLayer] = useState(false);

  const unHideAllLayers = useCallback(() => {
    setShouldHideParcelOwnerNames(false);
    setShouldHideParcelLayer(false);
    setShouldHideFsaLayer(false);
  }, []);

  const [parcelOrFsaToRender, setParcelOrFsa] = useState<ParcelOrFsa>(
    ParcelOrFsa.PARCELS
  );
  // Intercept the parcel/fsa setter.
  const setParcelOrFsaToRender: Dispatch<SetStateAction<ParcelOrFsa>> =
    useCallback(
      (val: SetStateAction<ParcelOrFsa>) => {
        unHideAllLayers();
        setParcelOrFsa(val);
      },
      [unHideAllLayers]
    );

  useEffect(() => {
    if (!getJsonFromLocalStorage(MAP_SETTINGS)) {
      saveJsonInLocalStorage(MAP_SETTINGS, {
        feature: ParcelOrFsa.PARCELS,
      });
    }
  }, []);

  const applyMapSettingsFromLocalStorage = useCallback(() => {
    if (isAnonymous) {
      setShouldHideParcelOwnerNames(true);
    } else {
      setParcelOrFsaToRender(
        localStorageMapSettings?.feature || ParcelOrFsa.PARCELS
      );
      setShouldHideParcelOwnerNames(!userCanAccessOwnerNames);
    }
  }, [
    isAnonymous,
    localStorageMapSettings?.feature,
    setParcelOrFsaToRender,
    userCanAccessOwnerNames,
  ]);

  useEffect(() => {
    if (!authStateLoading || !areMapSettingsReady)
      applyMapSettingsFromLocalStorage();
  }, [
    applyMapSettingsFromLocalStorage,
    areMapSettingsReady,
    authStateLoading,
    userCanAccessOwnerNames,
  ]);

  const shouldRenderParcelLayer = useMemo(
    () => parcelOrFsaToRender === ParcelOrFsa.PARCELS && !shouldHideParcelLayer,
    [parcelOrFsaToRender, shouldHideParcelLayer]
  );

  const shouldRenderFsaLayer = useMemo(
    () => parcelOrFsaToRender === ParcelOrFsa.FSA && !shouldHideFsaLayer,
    [parcelOrFsaToRender, shouldHideFsaLayer]
  );

  const shouldRenderParcelOwnerNames = useMemo(
    () =>
      userCanAccessOwnerNames &&
      !shouldHideParcelOwnerNames &&
      shouldRenderParcelLayer,
    [
      shouldRenderParcelLayer,
      shouldHideParcelOwnerNames,
      userCanAccessOwnerNames,
    ]
  );

  const manuallySetParcelOrFsaToRender = useCallback(
    (newVal: ParcelOrFsa) => {
      setParcelOrFsaToRender(newVal);
      saveSettingsInLocalStorage({ feature: newVal });
    },
    [saveSettingsInLocalStorage, setParcelOrFsaToRender]
  );

  return {
    parcelOrFsaToRender,
    setParcelOrFsaToRender: manuallySetParcelOrFsaToRender,
    setShouldHideFsaLayer,
    setShouldHideParcelLayer,
    setShouldHideParcelOwnerNames,
    shouldHideFsaLayer,
    shouldHideParcelLayer,
    shouldHideParcelOwnerNames,
    shouldRenderFsaLayer,
    shouldRenderParcelLayer,
    shouldRenderParcelOwnerNames,
  };
};
